// Covid19TermsModal.tsx
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Covid19TermsModal.css';

interface Covid19TermsModalProps {
  isOpen: boolean;
  onAcknowledge: () => void;
}

const Covid19TermsModal: React.FC<Covid19TermsModalProps> = ({
  isOpen,
  onAcknowledge
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const acknowledgeButtonRef = useRef<HTMLButtonElement>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);
  const noteSectionRef = useRef<HTMLDivElement>(null);
  const [hasReadToNote, setHasReadToNote] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Reset scroll state when modal opens
      setHasReadToNote(false);

      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal closes
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle scroll detection to enable button when user reaches Note section
  useEffect(() => {
    const handleScroll = () => {
      const content = modalContentRef.current;
      const noteSection = noteSectionRef.current;

      if (content && noteSection) {
        const contentRect = content.getBoundingClientRect();
        const noteRect = noteSection.getBoundingClientRect();

        // Check if note section is visible in the scrollable area (with 50px buffer)
        const isNoteVisible = noteRect.bottom <= contentRect.bottom + 50;

        // Check if user has scrolled down to the last part of the content
        const isAtBottom = content.scrollTop + content.clientHeight >= content.scrollHeight - 10;

        setHasReadToNote(isNoteVisible && isAtBottom);
      }
    };

    if (isOpen && modalContentRef.current) {
      const content = modalContentRef.current;
      content.addEventListener('scroll', handleScroll);

      // Check initial scroll position
      handleScroll();

      return () => {
        content.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="covid19-terms-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Before you proceed"
    >
      <div className="covid19-terms-modal" ref={modalRef}>
        <div className="covid19-terms-modal-content" ref={modalContentRef}>
          <div className="covid19-terms-section">
            <h3 className="covid19-terms-heading pb-5">Our Travel Sales Team is #HereForYou!</h3>

            <p>The plan you're looking for might not be available on MyTravelsafe at the moment. However, we'll be glad to help you with your other travel insurance needs. If you are interested in travel coverage for <span className="covid19-terms-highlight">seniors (age 76 and above)</span>, <span className="covid19-terms-highlight">groups</span> or <span className="covid19-terms-highlight">inbound trips to the Philippines</span>, please get in touch with us through e-mail <a href="mailto:traveldirect_team@pacificcross.com.ph" className="covid19-terms-email-link">traveldirect_team@pacificcross.com.ph</a> or telephone number <a href="tel:+6328899-8001" className="covid19-terms-email-link">+632 8899-8001</a>.</p>

            <h3 className="covid19-terms-heading py-5">Before you proceed to purchase any of our plans on MyTravelsafe, please note important details related to Cancellations and Refunds.</h3>

            <p>Pacific Cross will allow cancellations and consider refunds for the following:</p>

            <ul className="covid19-terms-bullets pt-5">
              <li><strong>Visa Denial.</strong> To request for a refund, the Insured Person should send an e-mail to Pacific Cross through <a href="mailto:traveldirect_team@pacificcross.com.ph" className="covid19-terms-email-link">traveldirect_team@pacificcross.com.ph</a>. The e-mail should include a copy (e.g., scanned or photograph) of the valid denial letter issued by the embassy.</li>

              <li><strong>Cancellation before the Policy Effective Date.</strong> A written request stating the reason for cancellation must be submitted by the Insured Person to Pacific Cross prior to the Policy's effective date. The request may be submitted via e-mail to <a href="mailto:traveldirect_team@pacificcross.com.ph" className="covid19-terms-email-link">traveldirect_team@pacificcross.com.ph</a>.</li>
            </ul>

            <div className='pt-3' ref={noteSectionRef}>
              <p><strong>Please note that all requests are subject to the Company's review and approval.</strong> Refunds will only be considered and allowed if all requirements are submitted <strong>before</strong> the Policy Effective Date. The amount of refund will be the unearned portion of the premium. Applicable cancellation fees will be deducted accordingly.</p>
            </div>
          </div>
        </div>

        <div className="covid19-terms-modal-footer">
          <button
            ref={acknowledgeButtonRef}
            className={`covid19-terms-acknowledge-btn ${!hasReadToNote ? 'disabled' : ''}`}
            onClick={onAcknowledge}
            disabled={!hasReadToNote}
          >
            I have read and understood the statements above.
          </button>
        </div>
      </div>
    </div>
  );

  // Render modal using React portal to ensure it's rendered at document body level
  return createPortal(modalContent, document.body);
};

export default Covid19TermsModal;
